/**
 * PermissionDegradedBanner.js — informational banner shown to non-owner
 * agents when background services have hit a 403 from the CRM.
 *
 * #168. Renders at the top of the chat list when:
 *   - $permissionDegraded.degraded === true
 *   - $permissionDegraded.dismissed === false
 *   - The session is non-owner (owners bypass all CRM permission
 *     checks; any 403 they see is a different problem we surface in
 *     console rather than this banner).
 *
 * Tone: informational, not alarming. The agent didn't do anything
 * wrong — the role just needs more permissions ticked.
 *
 * IMPORTANT — non-owner copy policy:
 *   Agents must not see any reference to the CRM web URL (chat.*.com)
 *   and must not have any link that takes them out of this PWA. The
 *   primary admin owns role configuration; the agent's only valid
 *   action is to ask the admin to fix it. So the banner has copy +
 *   a dismiss button — no CTA, no external link.
 */

import { html }     from 'htm/preact';
import { useStore } from '@nanostores/preact';

import { $session }                            from '../stores/session.js';
import { $permissionDegraded, dismissDegraded } from '../services/permission-degraded.js';

export function PermissionDegradedBanner() {
  const session = useStore($session);
  const state   = useStore($permissionDegraded);

  // Owners bypass — see service header.
  if (session?.userType === 'owner') return null;
  if (!state.degraded || state.dismissed) return null;

  return html`
    <div class="inbox-perm-degraded-banner" role="status" aria-live="polite">
      <div class="inbox-perm-degraded-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <div class="inbox-perm-degraded-body">
        <div class="inbox-perm-degraded-title">Some inbox features look limited</div>
        <div class="inbox-perm-degraded-sub">
          Your role doesn't have all the permissions the inbox needs.
          Please ask your account admin to grant the missing access on
          your role.
        </div>
      </div>
      <button
        class="inbox-perm-degraded-dismiss"
        onClick=${dismissDegraded}
        aria-label="Dismiss"
        title="Dismiss for this session"
      >×</button>
    </div>
  `;
}
